// Webhook auto-pause and scope (WOS-BUNDLE; Work OS `WEBHOOK_PAUSE_THRESHOLD = 5`,
// components/webhooks-page.tsx): a hook whose last five events each exhausted every retry is
// paused; a banner names it with Resume (`webhooks.resume`, its failure count back to 0). A hook
// can be scoped to a space or a board (`filter.container`): the list says "in <name>" or "Whole
// company", and the create / edit dialog picks it.
import {
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
  Notice,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlayIcon } from "lucide-react";
import { useId } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { boardsQuery, spacesQuery } from "../work/work-queries";

export type Scope = { kind: "space" | "board"; id: string } | null;
type HookLike = {
  id: string;
  url: string;
  status: string;
  consecutive_failures: number;
  filter: { container?: { kind: "space" | "board"; id: string } | undefined };
};

/** The company's spaces and boards by id (names for the scope), null while loading. */
export function useContainers(api: Api, co: string) {
  const spaces = useQuery(spacesQuery(api, co));
  const boards = useQuery(boardsQuery(api, co));
  return {
    spaces: (spaces.data?.data ?? []).filter((s) => !s.archived_at),
    boards: (boards.data?.data ?? []).filter((b) => !b.archived_at),
    nameOf: (c: NonNullable<Scope>) =>
      (c.kind === "space" ? spaces.data?.data : boards.data?.data)?.find((x) => x.id === c.id)
        ?.name ?? null,
  };
}

/** "Whole company", or "in Recruiting" / "in Hiring pipeline (board)". */
export function scopeLabel(
  filter: HookLike["filter"],
  nameOf: (c: NonNullable<Scope>) => string | null,
): string {
  const c = filter.container;
  if (!c) return "Whole company";
  return `in ${nameOf(c) ?? `a ${c.kind}`}${c.kind === "board" ? " (board)" : ""}`;
}

/** The banner over the list: each paused hook, why, and Resume. */
export function PausedBanner({
  api,
  company,
  hooks,
  onResumed,
}: {
  api: Api;
  company: CompanyAccess;
  hooks: HookLike[];
  onResumed: () => void;
}) {
  const qc = useQueryClient();
  const resume = useMutation({
    mutationFn: (id: string) => api.client.webhooks.resume({ params: { co: company.id, id } }),
    onSuccess: (h) => {
      onResumed();
      void qc.invalidateQueries({ queryKey: ["company", company.id, "webhooks"] });
      toast.add({ title: "Webhook resumed", description: h.url, type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The webhook was not resumed."), type: "error" }),
  });
  const paused = hooks.filter((h) => h.status === "paused");
  if (!paused.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {paused.map((h) => (
        <Notice key={h.id} tone="warning" data-slot="webhook-paused" data-webhook-paused={h.id}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="min-w-0 flex-1">
              <span className="font-mono text-xs break-all">{h.url}</span>
              <span className="block text-sm">
                Auto-paused after {Math.max(5, h.consecutive_failures)} events in a row failed all
                retries. Nothing is sent until you resume it.
              </span>
            </span>
            <Button
              size="sm"
              variant="secondary"
              aria-label={`Resume ${h.url}`}
              disabled={resume.isPending}
              onClick={() => resume.mutate(h.id)}
            >
              <PlayIcon aria-hidden="true" />
              Resume
            </Button>
          </div>
        </Notice>
      ))}
    </div>
  );
}

/** The create / edit dialog's scope picker. */
export function ScopeField({
  api,
  co,
  value,
  onChange,
}: {
  api: Api;
  co: string;
  value: Scope;
  onChange: (scope: Scope) => void;
}) {
  const id = useId();
  const { spaces, boards } = useContainers(api, co);
  const encoded = value ? `${value.kind}:${value.id}` : "";
  return (
    <Field>
      <FieldLabel htmlFor={id}>Scope</FieldLabel>
      <NativeSelect
        id={id}
        value={encoded}
        onChange={(e) => {
          const [kind, cid] = e.target.value.split(":");
          onChange(kind && cid ? { kind: kind as "space" | "board", id: cid } : null);
        }}
      >
        <NativeSelectOption value="">Whole company</NativeSelectOption>
        {spaces.length ? (
          <NativeSelectOptGroup label="A space">
            {spaces.map((s) => (
              <NativeSelectOption key={s.id} value={`space:${s.id}`}>
                {s.name}
              </NativeSelectOption>
            ))}
          </NativeSelectOptGroup>
        ) : null}
        {boards.length ? (
          <NativeSelectOptGroup label="A board">
            {boards.map((b) => (
              <NativeSelectOption key={b.id} value={`board:${b.id}`}>
                {b.name}
              </NativeSelectOption>
            ))}
          </NativeSelectOptGroup>
        ) : null}
      </NativeSelect>
      <FieldDescription>
        Only events inside it are sent: the space or board, its boards, tasks and views.
      </FieldDescription>
    </Field>
  );
}
